/**
 * EF-API-ENDPOINT-CONSISTENCY-GUARDRAIL-V1
 * 
 * Centralized API endpoint definitions to prevent inconsistencies
 * between frontend calls and backend routes.
 * 
 * USAGE:
 *   import { API_ENDPOINTS } from '@/api/endpoints';
 *   
 *   // Static endpoint
 *   fetch(`${API_URL}${API_ENDPOINTS.crew.documents}`);
 *   
 *   // Dynamic endpoint
 *   fetch(`${API_URL}${API_ENDPOINTS.staff.getById(staffId)}`);
 * 
 * ADDING NEW ENDPOINTS:
 *   1. Add the endpoint to the appropriate category below
 *   2. Use the centralized constant in your component
 *   3. Never hardcode API paths directly in components
 */

// =============================================================================
// AUTHENTICATION
// =============================================================================
export const AUTH_ENDPOINTS = {
  login: "/api/auth/login",
  session: "/api/auth/session",
  requestPasswordReset: "/api/auth/request-password-reset",
  resetPassword: "/api/auth/reset-password",
  validateResetToken: (token, userType) => 
    `/api/auth/validate-reset-token?token=${encodeURIComponent(token)}&type=${userType}`,
};

// =============================================================================
// CREW ONBOARDING & PORTAL
// =============================================================================
export const CREW_ENDPOINTS = {
  // Onboarding flow
  validateToken: (token) => 
    `/api/crew/onboarding/validate-token?token=${encodeURIComponent(token)}`,
  setPassword: (token) => 
    `/api/crew/onboarding/set-password?token=${encodeURIComponent(token)}`,
  
  // Documents
  uploadDocument: "/api/crew/onboarding/upload-document",
  getDocuments: "/api/crew/onboarding/documents",
  deleteDocument: (id) => `/api/crew/documents/${id}`,  // EF-FIX-CREW-DOCUMENT-LIST-AND-DELETE-V1
  onboardingStatus: "/api/crew/onboarding/status",
  
  // Profile (for crew candidates)
  // EF-FIX-CREW-ONBOARDING-PHONE-FORM-STATE-V1: Added getProfile endpoint
  getProfile: "/api/crew/onboarding/profile",  // Returns merged staff + candidate data
  updateProfile: "/api/crew/onboarding/profile",  // PATCH to update profile
  
  // Company info (tenant-aware)
  companyInfo: "/api/crew/company-info",
  // EVENTSFLO-CREW-PORTAL-I18N-PERSIST-PREFERENCE-V1
  // Persisted Crew Portal UI language preference (en|fr|ru|null).
  // Crew Portal hydrates from GET on load and writes back on every change.
  getPreferredLanguage: "/api/crew/me/preferred-language",
  setPreferredLanguage: "/api/crew/me/preferred-language",
  
  // EF-CREW-PWA-PHASE-1-SHIFT-INTERACTION-V1: Crew shift endpoints
  shifts: "/api/crew/shifts",
  acceptShift: (shiftId) => `/api/crew/shifts/${shiftId}/accept`,
  rejectShift: (shiftId) => `/api/crew/shifts/${shiftId}/reject`,
  
  // Candidates management (admin)
  invite: "/api/crew/invite",
  listCandidates: "/api/crew/candidates",
  getCandidate: (id) => `/api/crew/candidates/${id}`,
  resendInvitation: (id) => `/api/crew/candidates/${id}/resend-invitation`,
  approveCandidate: (id) => `/api/crew/candidates/${id}/approve`,
  rejectCandidate: (id) => `/api/crew/candidates/${id}/reject`,
  getCandidateDocuments: (id) => `/api/admin/crew-candidates/${id}/documents`,
  
  // EF-CREW-ONBOARDING-STATUS-AND-DOCUMENT-APPROVAL-V1: Admin document management
  adminDocuments: "/api/admin/crew-documents",
  approveDocument: (id) => `/api/admin/crew-documents/${id}/approve`,
  rejectDocument: (id) => `/api/admin/crew-documents/${id}/reject`,
  
  // EF-FIX-DOCUMENT-PREVIEW-UNKNOWN-TYPE-V2: Document file endpoints
  getDocumentFile: (id) => `/api/crew/documents/${id}/file`,
  getAdminDocumentFile: (id) => `/api/admin/crew-documents/${id}/file`,
};

// =============================================================================
// STAFF MANAGEMENT
// =============================================================================
export const STAFF_ENDPOINTS = {
  list: "/api/staff",
  create: "/api/staff",
  getById: (id) => `/api/staff/${id}`,
  update: (id) => `/api/staff/${id}`,
  delete: (id) => `/api/staff/${id}`,
  
  // Invitations
  sendInvite: (id) => `/api/staff/${id}/invite`,
  sendPasswordReset: (id) => `/api/staff/${id}/send-password-reset`,
  
  // Documents
  uploadProfilePhoto: (id) => `/api/staff/${id}/upload-profile-photo`,
  
  // Qualifications
  updateQualifications: (id) => `/api/staff/${id}/qualifications`,
  
  // Travel supplements
  travelSupplements: (id) => `/api/staff/${id}/travel-supplements`,
};

// =============================================================================
// ASSETS & DOCUMENTS
// =============================================================================
export const ASSET_ENDPOINTS = {
  // Note: Crew documents use CREW_ENDPOINTS.uploadDocument
  // This is for admin asset management
  list: "/api/assets",
  getById: (id) => `/api/assets/${id}`,
  getByIds: (ids) => `/api/assets?ids=${ids.join(",")}`,
  inboxAttachment: (id) => `/api/inbox/attachments/${id}/file`,
};

// =============================================================================
// COMPANY & SETTINGS
// =============================================================================
export const SETTINGS_ENDPOINTS = {
  company: "/api/settings/company",
  companyProfile: "/api/company-profile",
  updateCompanyProfile: "/api/company-profile",
};

// =============================================================================
// INTEGRATIONS
// =============================================================================
export const INTEGRATION_ENDPOINTS = {
  google: {
    status: (companyId) => `/api/integrations/google/status?company_id=${companyId}`,
    routesApiKey: "/api/integrations/google/routes/api-key",
    routesApiKeyWithCompany: (companyId) => 
      `/api/integrations/google/routes/api-key?company_id=${companyId}`,
    testRoutes: (companyId) => `/api/integrations/google/test-routes?company_id=${companyId}`,
    visionCredentials: "/api/integrations/google/vision/credentials",
    visionCredentialsWithCompany: (companyId) => 
      `/api/integrations/google/vision/credentials?company_id=${companyId}`,
    testVision: (companyId) => `/api/integrations/google/test-vision?company_id=${companyId}`,
  },
};

// =============================================================================
// SHIFTS & SCHEDULING
// =============================================================================
export const SHIFT_ENDPOINTS = {
  list: "/api/shifts",
  create: "/api/shifts",
  getById: (id) => `/api/shifts/${id}`,
  update: (id) => `/api/shifts/${id}`,
  delete: (id) => `/api/shifts/${id}`,
  getCrew: (shiftId) => `/api/shifts/${shiftId}/crew`,
  updateCrewAssignment: (shiftId, staffId) => `/api/shifts/${shiftId}/crew/${staffId}`,
};

// =============================================================================
// PROJECTS & VENUES
// =============================================================================
export const PROJECT_ENDPOINTS = {
  list: "/api/projects",
  create: "/api/projects",
  getById: (id) => `/api/projects/${id}`,
  update: (id) => `/api/projects/${id}`,
};

export const VENUE_ENDPOINTS = {
  list: "/api/venues",
  create: "/api/venues",
  getById: (id) => `/api/venues/${id}`,
  update: (id) => `/api/venues/${id}`,
};

// =============================================================================
// DASHBOARD & METRICS
// =============================================================================
export const DASHBOARD_ENDPOINTS = {
  metrics: "/api/dashboard/metrics",
  health: "/api/admin/platform-health",
};

// =============================================================================
// COMBINED EXPORT FOR CONVENIENCE
// =============================================================================
export const API_ENDPOINTS = {
  auth: AUTH_ENDPOINTS,
  crew: CREW_ENDPOINTS,
  staff: STAFF_ENDPOINTS,
  assets: ASSET_ENDPOINTS,
  settings: SETTINGS_ENDPOINTS,
  integrations: INTEGRATION_ENDPOINTS,
  shifts: SHIFT_ENDPOINTS,
  projects: PROJECT_ENDPOINTS,
  venues: VENUE_ENDPOINTS,
  dashboard: DASHBOARD_ENDPOINTS,
};

// =============================================================================
// DEV MODE: Endpoint validation helper
// =============================================================================
export function validateEndpoint(endpoint, componentName = "Unknown") {
  if (process.env.NODE_ENV === "development") {
    // Check if endpoint starts with /api/
    if (!endpoint.startsWith("/api/")) {
      console.warn(
        `[API Guardrail] Invalid endpoint in ${componentName}: "${endpoint}" should start with /api/`
      );
    }
    
    // Check for common mistakes
    const commonMistakes = {
      "/api/assets/upload": "Use API_ENDPOINTS.crew.uploadDocument for crew uploads",
      "/api/upload": "Use appropriate upload endpoint from API_ENDPOINTS",
    };
    
    if (commonMistakes[endpoint]) {
      console.warn(
        `[API Guardrail] Deprecated endpoint in ${componentName}: "${endpoint}"\n` +
        `Suggestion: ${commonMistakes[endpoint]}`
      );
    }
  }
  return endpoint;
}

export default API_ENDPOINTS;
